import { Preferences } from '@capacitor/preferences';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import useToast from '@/composables/useToast';
import { authService } from '@/services/AuthService';
import { API_CONSTANTS, type ApiConfig, type ApiError } from '@/types/apiTypes';
import { loadingHandler } from '@/utils/loadingHandler';

export function createApiInstance(): AxiosInstance {
    const api = axios.create({
        baseURL: API_CONSTANTS.BASE_URL,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Platform-Access': API_CONSTANTS.PLATFORM_ACCESS,
            'X-Api-Key': API_CONSTANTS.API_KEY,
        },
    });

    setupInterceptors(api);
    return api;
}

function setupInterceptors(api: AxiosInstance): void {
    api.interceptors.request.use(
        async (config: ApiConfig) => {
            try {
                // Handle loading state
                if (!config.headers?.['Skip-Loader']) {
                    loadingHandler.startLoading();
                    // Emit a custom event that the LoadingProvider will listen to
                    window.dispatchEvent(new CustomEvent('api-loading-start'));
                }

                // Handle authentication
                const token = await Preferences.get({ key: API_CONSTANTS.TOKEN_KEY });
                if (config.headers && token.value) {
                    config.headers.Authorization = `Bearer ${token.value}`;
                }

                return config;
            } catch (error) {
                console.error('Error in request interceptor:', error);
                if (loadingHandler.stopLoading()) {
                    window.dispatchEvent(new CustomEvent('api-loading-stop'));
                }
                return config;
            }
        },
        (error: ApiError) => {
            if (loadingHandler.stopLoading()) {
                window.dispatchEvent(new CustomEvent('api-loading-stop'));
            }
            console.error('Request interceptor error:', error);
            return Promise.reject(error);
        },
    );

    api.interceptors.response.use(
        (response: AxiosResponse) => {
            if (!response.config.headers?.['Skip-Loader']) {
                if (loadingHandler.stopLoading()) {
                    window.dispatchEvent(new CustomEvent('api-loading-stop'));
                }
            }
            return response;
        },
        async (error: ApiError) => {
            if (!error.config?.headers?.['Skip-Loader']) {
                if (loadingHandler.stopLoading()) {
                    window.dispatchEvent(new CustomEvent('api-loading-stop'));
                }
            }

            const { openToast } = useToast();

            if (!error.response) {
                openToast('Network error. Please check your connection.', 'danger', 'top');
                return Promise.reject(error);
            }

            switch (error.response.status) {
                case 401:
                    // Clear session but DON'T force-redirect to login.
                    // The auth guard will redirect on the next navigation to a protected route.
                    // This prevents stray 401s (e.g. profile/edit during registration)
                    // from breaking the registration flow.
                    try {
                        await authService.clearSession();
                    } catch (e) {
                        console.error('Error handling unauthorized:', e);
                    }
                    break;

                case 403:
                    if (error.response.data?.reason === 'fraud') {
                        window.dispatchEvent(
                            new CustomEvent('fraud-alert', {
                                detail: { message: error.response.data?.message || 'Account flagged for unusual activity.' },
                            }),
                        );
                    } else {
                        openToast(
                            'You do not have permission to perform this action.',
                            'danger',
                            'top',
                        );
                    }
                    break;

                case 404:
                    openToast('The requested resource was not found.', 'danger', 'top');
                    break;

                case 500:
                    openToast(
                        'An unexpected error occurred. Please try again later.',
                        'danger',
                        'top',
                    );
                    break;

                default:
                    openToast(
                        error.response.data?.message || error.response.data?.error || 'An error occurred.',
                        'danger',
                        'top',
                    );
            }

            return Promise.reject(error);
        },
    );
}
